import React, { useState } from "react";
import { Share2, Copy, Check, MessageCircle } from "lucide-react";
import { toast } from "sonner";

/**
 * ShareButtons - botões de Compartilhar (WhatsApp) + Copiar link.
 *
 * Props:
 *  - url:        URL completa a compartilhar (default: window.location.href)
 *  - productSlug: se informado, o link compartilhado roteará por /api/og/{slug}
 *                 para ter preview rico (imagem+nome+preço) no WhatsApp/FB/IG
 *  - title:      Texto/headline que vai na frente do link (ex: "Olha esse produto!")
 *  - layout:     "row" (default) | "stacked" | "icons"
 *  - variant:    "light" (default, p/ fundos claros) | "dark" (p/ fundos escuros)
 *  - testIdPrefix: prefixo p/ data-testid
 */
export const ShareButtons = ({
    url,
    productSlug = "",
    title = "",
    layout = "row",
    variant = "light",
    testIdPrefix = "share",
    className = "",
}) => {
    const [copied, setCopied] = useState(false);

    // For products we share the OG-enriched backend URL (rich preview in messengers).
    // For everything else (home/footer) we share the direct URL.
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const finalUrl = productSlug
        ? `${origin}/api/og/${productSlug}`
        : (url || (typeof window !== "undefined" ? window.location.href : ""));
    const message = title ? `${title}\n${finalUrl}` : finalUrl;

    const shareWhatsApp = () => {
        const wa = `https://wa.me/?text=${encodeURIComponent(message)}`;
        window.open(wa, "_blank", "noopener,noreferrer");
    };

    const shareNative = async () => {
        try {
            if (navigator.share) {
                await navigator.share({ title, text: title, url: finalUrl });
            } else {
                shareWhatsApp();
            }
        } catch (e) {
            // user cancelled — ignore
        }
    };

    const copyLink = async () => {
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(finalUrl);
            } else {
                // Fallback for non-https / older browsers
                const ta = document.createElement("textarea");
                ta.value = finalUrl;
                ta.style.position = "fixed";
                ta.style.opacity = "0";
                document.body.appendChild(ta);
                ta.select();
                document.execCommand("copy");
                document.body.removeChild(ta);
            }
            setCopied(true);
            toast.success("Link copiado! Cole em qualquer rede social.");
            setTimeout(() => setCopied(false), 2200);
        } catch (e) {
            toast.error("Não foi possível copiar. Selecione manualmente:");
            window.prompt("Copie este link:", finalUrl);
        }
    };

    if (layout === "icons") {
        return (
            <div className={`inline-flex items-center gap-2 ${className}`}>
                <button
                    onClick={shareWhatsApp}
                    data-testid={`${testIdPrefix}-wa`}
                    title="Compartilhar no WhatsApp"
                    aria-label="Compartilhar no WhatsApp"
                    className="w-9 h-9 inline-flex items-center justify-center bg-brand-success text-white hover:bg-brand-success/90 rounded-full transition-colors"
                >
                    <MessageCircle className="w-4 h-4" />
                </button>
                <button
                    onClick={copyLink}
                    data-testid={`${testIdPrefix}-copy`}
                    title="Copiar link"
                    aria-label="Copiar link"
                    className={`w-9 h-9 inline-flex items-center justify-center rounded-full transition-colors ${copied ? "bg-brand-success text-white" : "bg-brand-ink text-white hover:bg-brand-dark"}`}
                >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
            </div>
        );
    }

    const stack = layout === "stacked";
    const copyClasses = copied
        ? "bg-brand-success/10 text-brand-success border-brand-success"
        : variant === "dark"
            ? "border-white/30 text-white hover:border-white hover:bg-white/10"
            : "border-brand-border text-brand-ink hover:border-brand-ink";
    return (
        <div className={`${stack ? "flex flex-col" : "flex flex-row"} gap-2 ${className}`}>
            <button
                onClick={shareWhatsApp}
                data-testid={`${testIdPrefix}-wa`}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-brand-success text-white hover:bg-brand-success/90 px-4 py-3 text-sm font-bold rounded-sm transition-colors"
            >
                <MessageCircle className="w-4 h-4" />
                Compartilhar no WhatsApp
            </button>
            <button
                onClick={copyLink}
                data-testid={`${testIdPrefix}-copy`}
                className={`flex-1 inline-flex items-center justify-center gap-2 border-2 px-4 py-3 text-sm font-bold rounded-sm transition-colors ${copyClasses}`}
            >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? "Link copiado!" : "Copiar link"}
            </button>
            {/* Hidden but available for mobile native share */}
            {typeof navigator !== "undefined" && navigator.share && (
                <button
                    onClick={shareNative}
                    data-testid={`${testIdPrefix}-native`}
                    title="Mais opções de compartilhamento"
                    aria-label="Mais opções"
                    className="inline-flex items-center justify-center gap-2 border-2 border-brand-border text-brand-ink hover:border-brand-ink px-4 py-3 text-sm font-bold rounded-sm transition-colors sm:hidden"
                >
                    <Share2 className="w-4 h-4" />
                </button>
            )}
        </div>
    );
};

export default ShareButtons;
